import React, { useRef, useCallback } from 'react';
import logoSc from '@/assets/logo-sc.png';

interface LeftSidePanelProps {
  /** Tenant primary color in hex e.g. "#1b1450". Defaults to brand indigo. */
  primaryColor?: string;
  /** Flat background override (any CSS color). When set, replaces the primaryColor gradient —
   *  used by the app nav drawer so it matches the AI Chat dark surface instead of a lighter tint. */
  background?: string;
  /** Tenant/app name displayed next to the logo. Defaults to "Smart\nContractor". */
  appName?: string;
  /** Optional logo override — rendered instead of the default house+chat SVG. */
  logoIcon?: React.ReactNode;
  className?: string;
  /** Panel width in px. Defaults to 276. */
  width?: number;
  /**
   * Interactive body (e.g. navigation links). When provided, the panel renders
   * these in place of the decorative feature callouts and becomes a real,
   * focusable navigation region (no longer aria-hidden / md-only).
   */
  children?: React.ReactNode;
  /** Optional bottom slot pinned below the body (e.g. a Settings link). */
  footer?: React.ReactNode;
  /** When true, inverts chrome colors (divider, footer border, box shadow) for a white/light background. */
  lightMode?: boolean;
}

// ── Color helpers ────────────────────────────────────────────────────────────

function hexToRGB(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

function shiftRGB(hex: string, d: number): string {
  const { r, g, b } = hexToRGB(hex);
  return `rgb(${clamp(r + d)},${clamp(g + d)},${clamp(b + d)})`;
}

// ── Default logo (SmartContractor brand mark) ────────────────────────────────

const DefaultLogoIcon: React.FC = () => (
  <img
    src={logoSc}
    alt="SmartContractor"
    style={{
      width: 170,
      height: 'auto',
      maxWidth: '100%',
      objectFit: 'contain',
      display: 'block',
    }}
  />
);

// ── Nav items (feature callouts — decorative on auth pages) ─────────────────

const NAV_ITEMS = [
  {
    label: 'Project Tracking',
    description: 'Manage jobs, crews & timelines in one place.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
  },
  {
    label: 'Voice-First Design',
    description: 'Let SAM handle notes, tasks & updates for you.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
           strokeLinecap="round" width="20" height="20">
        <line x1="3"  y1="12" x2="3"  y2="12"/>
        <line x1="6"  y1="9"  x2="6"  y2="15"/>
        <line x1="9"  y1="5"  x2="9"  y2="19"/>
        <line x1="12" y1="8"  x2="12" y2="16"/>
        <line x1="15" y1="6"  x2="15" y2="18"/>
        <line x1="18" y1="9"  x2="18" y2="15"/>
        <line x1="21" y1="11" x2="21" y2="13"/>
      </svg>
    ),
  },
  {
    label: 'Secure & Compliant',
    description: 'Enterprise-grade security with full audit trails.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
];

// ── Component ────────────────────────────────────────────────────────────────

const LeftSidePanel: React.FC<LeftSidePanelProps> = ({
  primaryColor = '#1b1450',
  background,
  appName = 'Smart\nContractor',
  logoIcon,
  className = '',
  width = 280,
  children,
  footer,
  lightMode = false,
}) => {
  const interactive = children != null;
  const spotlightRef = useRef<HTMLDivElement>(null);
  const spotVisible = useRef(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (lightMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (spotlightRef.current) {
      spotlightRef.current.style.background =
        `radial-gradient(circle 260px at ${x}px ${y}px, ` +
        'rgba(255,255,255,0.18) 0%, ' +
        'rgba(255,255,255,0.07) 40%, ' +
        'transparent 68%)';
      if (!spotVisible.current) {
        spotlightRef.current.style.opacity = '1';
        spotVisible.current = true;
      }
    }
  }, [lightMode]);

  const handleMouseLeave = useCallback(() => {
    if (spotlightRef.current) {
      spotlightRef.current.style.opacity = '0';
      spotVisible.current = false;
    }
  }, []);

  const c1 = shiftRGB(primaryColor, -14);
  const c2 = shiftRGB(primaryColor, 0);
  const c3 = shiftRGB(primaryColor, 38);

  // Render appName with newline support
  const nameLines = appName.split('\n');

  return (
    <div
      className={`${interactive ? 'flex h-full' : 'hidden lg:flex min-h-screen'} relative flex-shrink-0 flex-col overflow-hidden select-none ${className}`}
      style={{
        width,
        background: background ?? `linear-gradient(168deg, ${c1} 0%, ${c2} 52%, ${c3} 100%)`,
        boxShadow: lightMode ? '4px 0 12px rgba(0,0,0,0.06)' : '4px 0 32px rgba(0,0,0,0.28)',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      aria-hidden={interactive ? undefined : 'true'}
      data-left-side-panel=""
    >
      {/* Logo area — the brand logo already includes the wordmark, so no
          separate app-name text is rendered. A custom logoIcon (e.g. tenant
          logo) renders alongside the app name; the default logo stands alone. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: logoIcon ? 20 : 0,
        padding: '28px 16px 24px',
        minWidth: width,
        flexShrink: 0,
      }}>
        {logoIcon ? (
          <>
            <div style={{ flexShrink: 0, width: 40, height: 40 }}>{logoIcon}</div>
            <span
              className="left-side-panel-logo-text"
              style={{
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.2,
                // Long tenant names wrap onto multiple lines (and break mid-word if needed) rather
                // than overflowing the panel; clamp to 3 lines with an ellipsis for extreme names.
                flex: 1,
                minWidth: 0,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
              }}
            >
              {nameLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < nameLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          </>
        ) : (
          <DefaultLogoIcon />
        )}
      </div>

      {/* Divider — uses sidebar border token so it adapts to light/dark mode */}
      <div style={{
        height: 1,
        margin: '0 14px 12px',
        background: lightMode ? 'rgba(0,0,0,0.08)' : 'rgba(var(--ui-sidebar-border, 255 255 255) / 0.12)',
        minWidth: width - 28,
        flexShrink: 0,
      }} />

      {/* Body — interactive nav (children) or decorative feature callouts */}
      {interactive ? (
        <nav className="relative z-[2] flex-1 overflow-y-auto no-scrollbar px-3 py-1">
          {children}
        </nav>
      ) : (
        <nav style={{ flex: 1, overflow: 'hidden' }}>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3, padding: '0 10px' }}>
            {NAV_ITEMS.map((item, i) => (
              <li key={item.label}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 13,
                  padding: '10px 13px',
                  borderRadius: 11,
                  cursor: 'default',
                }}>
                  {/* Icon chip */}
                  <div style={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    background: 'rgba(255,255,255,0.09)',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {item.icon}
                  </div>
                  {/* Label + description */}
                  <div
                    className={`left-side-panel-label left-side-panel-label-delay-${i}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}
                  >
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                      {item.label}
                    </span>
                    {'description' in item && (
                      <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 400, lineHeight: 1.4 }}>
                        {(item as typeof item & { description: string }).description}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Footer slot (pinned below body) */}
      {footer && (
        <div
          className="relative z-[2] px-3 py-3"
          style={{ borderTop: lightMode ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(var(--ui-sidebar-border, 255 255 255) / 0.12)' }}
        >
          {footer}
        </div>
      )}

      {/* Cursor spotlight overlay */}
      <div
        ref={spotlightRef}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 600ms ease',
          zIndex: 1,
        }}
      />

      {/* Bottom decorative glow rings — hidden in light mode */}
      <div style={{ display: lightMode ? 'none' : undefined,
        position: 'absolute',
        inset: 'auto 0 0 0',
        height: 160,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          bottom: -80,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 320,
          height: 180,
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.06)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -120,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 440,
          height: 240,
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.04)',
        }} />
      </div>

      {/* Scoped CSS for hover-expand and staggered label animations */}
      <style>{`
        [data-left-side-panel=""] {
          font-family: 'Poppins', system-ui, sans-serif;
        }
        .left-side-panel-logo-text { opacity: 1; }
        .left-side-panel-label { opacity: 1; }
      `}</style>
    </div>
  );
};

export default LeftSidePanel;
